/**
 * Запросы мастера «Пересоздать карточки» под лимит nginx на проде.
 *
 * Мастер шлёт по запросу на макет, один за другим. Перед /api на проде стоит
 * `limit_req zone=api burst=20 nodelay`: около двадцати запросов подряд проходят,
 * следующие nginx отбивает кодом 503 со своей HTML-страницей. 01.10.2026 на этом
 * упал первый же шаг мастера при выборке из 28 макетов — и вся выборка вместе с
 * ним. Поэтому между запросами пауза, а отбитый запрос повторяется с нарастающей
 * задержкой.
 *
 * Повторяем только то, что заведомо не дошло до приложения: 429 и 503 без
 * JSON-ответа бэкенда. 503 с телом `{ success: false, … }` пришёл от самого
 * гейтвея — его повтор ничего не исправит, и такой ответ отдаём как есть.
 */

export const REQUEST_GAP_MS = 250
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 8000]

export const isRateLimited = (error: any): boolean => {
  const status = error?.response?.status
  if (status === 429) return true
  if (status !== 503) return false
  const data = error?.response?.data
  return !(data && typeof data === 'object' && 'success' in data)
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export interface PacedCaller {
  <T>(request: () => Promise<T>): Promise<T>
}

/**
 * Возвращает функцию-обёртку: каждый вызов ждёт, пока с предыдущего пройдёт
 * `gapMs`, и повторяет запрос при отбое по лимиту. Одна обёртка — на один
 * проход по макетам.
 */
export function createPacedCaller(options: {
  gapMs?: number
  retryDelaysMs?: number[]
  sleep?: (ms: number) => Promise<void>
  now?: () => number
} = {}): PacedCaller {
  const gapMs = options.gapMs ?? REQUEST_GAP_MS
  const retryDelays = options.retryDelaysMs ?? RETRY_DELAYS_MS
  const sleep = options.sleep ?? defaultSleep
  const now = options.now ?? (() => Date.now())
  let lastStartedAt: number | null = null

  return async <T,>(request: () => Promise<T>): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
      if (lastStartedAt !== null) {
        const wait = lastStartedAt + gapMs - now()
        if (wait > 0) await sleep(wait)
      }
      lastStartedAt = now()
      try {
        return await request()
      } catch (error) {
        if (!isRateLimited(error) || attempt >= retryDelays.length) throw error
        await sleep(retryDelays[attempt])
      }
    }
  }
}
